import React, { useState, useEffect } from "react";

import Tab from '@mui/material/Tab';
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TabPanel from '@mui/lab/TabPanel';
import Divider from "@mui/material/Divider";
import TabContext from '@mui/lab/TabContext';
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { useDispatch, useSelector } from "react-redux";

import BasicInfoTab from "./BasicInfoTab";
import TellStoryTab from "./TellStoryTab";
import useForm from "../../../hooks/UseForm";
import { resizeFile } from "../../../helpers/CropImage";
import Tracking from "../../../services/tracking.service";
import { fundRaiserPageAction } from "../../../store/actions";
import { StyledFundraiserForm } from "./FundraiserFormStyles";
import { UserService } from "../../../services/storage.service";
import { InfoIcon, MegaPhoneIcon } from "../../../components/Icons";
import { useNonInitialEffect } from "../../../hooks/UseNonInitialEffect";
import { validateFundraiserForm } from "../../../validations/CreateEditFundraiserValidations";
import { Footer, Header, MixPanelTrackedLink, ShowSpinnerOrLabel } from "../../../components";
import { DEFAULT_COUNTRY, DRAFT_PAGE, FUNDRAISER_TABS, GTM_EVENTS, MISSION_STATEMENT, PRIVATE_PAGE, PUBLIC_PAGE, SINGLE_OWNER_PAGE } from "../../../constants";

export default function ({ editView = false, pageDetails = {} }) {

    const { BASIC_INFO, TELL_A_STORY } = FUNDRAISER_TABS

    const [selectedTab, changeSelectedTab] = useState(BASIC_INFO);
    const [publishFundraiserInitiated, setPublishFundraiserInitiated] = useState(false);
    const [draftingFundraiserInitiated, setDraftingFundraiserInitiated] = useState(false);

    const dispatch = useDispatch();

    const errors = useSelector(state => state.validationErrors);
    const campaigns = useSelector(state => state.campaigns.campaigns);
    const occasions = useSelector(state => state.occasions.occasions);
    const eventId = useSelector(state => state.get_event_id.event_id.data);
    const productImpact = useSelector(state => state.donation.product_impact);
    const defaultImages = useSelector(state => state.fundraiser_page.default_images);

    const [state, setState] = useState({
        name: "",
        goal: "",
        pageType: SINGLE_OWNER_PAGE,
        keepPagePrivate: false,
        occasion: 0,
        campaign: 0,
        cover: null,
        selectedImage: {
            default: true,
            path: null,
            file: null
        },
        missionStatement: {
            story: MISSION_STATEMENT,
            storyWithTags: null
        },
        fundraiseForFlood: false,
        selectedCurrency: {
            ...DEFAULT_COUNTRY
        },
    })

    const handleStateChange = (event, currencyChange = false) => {
        if (currencyChange)
            setState({
                ...state,
                selectedCurrency: {
                    id: event.currency_id,
                    name: event.country_name,
                    countryId: event.db_id || event.country_id
                }
            })

        else {
            const { name, value } = event.target;

            switch (name) {
                case "keepPagePrivate":
                case "fundraiseForFlood":
                    setState({
                        ...state,
                        [name]: event.target.checked
                    })
                    break;

                case "campaign":
                    /**
                     * In create mode, if the campaign has its own cover image and story, upon selecting that campaign the fundraiser 
                     * story and cover will get overriden by campaign's cover and image.
                     * In edit mode, only campaign value will get updated.
                     */
                    if (editView) {
                        setState({
                            ...state,
                            [name]: value ? value.id : 0,
                            fundraiseForFlood: value && value.is_special ? true : false,
                        })
                    }
                    else {
                        let missionStatement = MISSION_STATEMENT;

                        if (value?.fundraiser_default_story)
                            missionStatement = value.fundraiser_default_story

                        setState({
                            ...state,
                            [name]: value ? value.id : 0,
                            fundraiseForFlood: value && value.is_special ? true : false,
                            selectedImage: {
                                ...state.selectedImage,
                                path: value.fundraiser_default_image ? value.fundraiser_default_image : defaultImages[0].path,
                            },
                            missionStatement: {
                                ...state.missionStatement,
                                story: missionStatement
                            }
                        })
                    }
                    break;

                case "occasion":
                    setState({
                        ...state,
                        [name]: value ? value.id : 0,
                    })
                    break;

                case "selectedImage":
                    setState({
                        ...state,
                        selectedImage: {
                            ...value
                        }
                    })

                default:
                    setState({
                        ...state,
                        [name]: value
                    })
                    break;
            }
        }
    }

    const updateSelectedTab = (event) => {
        changeSelectedTab(Number(event.target.id));
    };

    const mapPageButtons = () => {

        switch (selectedTab) {
            case BASIC_INFO:
                return <MixPanelTrackedLink onClick={handleSubmit} variant="contained" type="button" label="Next" context="Next button on create/update fundraiser page">
                    Next
                </MixPanelTrackedLink>

            case TELL_A_STORY:
                return <React.Fragment>
                    {
                        !editView || pageDetails?.status == DRAFT_PAGE ? <MixPanelTrackedLink
                            type="button"
                            variant="outlined"
                            label='Save For Later'
                            context="Create Fundraiser Page"
                            disabled={draftingFundraiserInitiated || publishFundraiserInitiated}
                            onClick={() => handleSubmit(DRAFT_PAGE)}
                        >
                            <ShowSpinnerOrLabel label="Save For Later" loading={draftingFundraiserInitiated} />
                        </MixPanelTrackedLink> : null
                    }
                    <MixPanelTrackedLink
                        type="button"
                        label='Publish'
                        variant="contained"
                        context="Create Fundraiser Page"
                        onClick={() => handleSubmit(PUBLIC_PAGE)}
                        disabled={publishFundraiserInitiated || draftingFundraiserInitiated}
                    >
                        <ShowSpinnerOrLabel label={editView && pageDetails?.status !== DRAFT_PAGE ? "Update" : "Publish"} loading={publishFundraiserInitiated} variant='white' />
                    </MixPanelTrackedLink>
                </React.Fragment>
            default:
                break;
        }
    }

    const publishFundraiser = async (pageStatus = PUBLIC_PAGE) => {

        switch (selectedTab) {
            case BASIC_INFO:
                Tracking.trackGTM(
                    {
                        event: GTM_EVENTS.BASIC_INFO_DONE,
                        page_name: state.name,
                        country: state.selectedCurrency.name,
                        goal_amount: state.goal,
                        fundraiser_type: state.pageType == SINGLE_OWNER_PAGE ? "Individual Page" : "Team Page",
                        visibility: state.keepPagePrivate ? "Private" : "Public",
                    }
                )
                changeSelectedTab(TELL_A_STORY);
                break;

            case TELL_A_STORY:

                let status = pageStatus, pageStatusString, gtmEvent;

                if (pageStatus == DRAFT_PAGE) {
                    setDraftingFundraiserInitiated(true);;
                    pageStatusString = "Draft";
                }

                else {
                    setPublishFundraiserInitiated(true)
                    if (state.keepPagePrivate) {
                        status = PRIVATE_PAGE;
                        pageStatusString = "Private";
                    }

                    else
                        pageStatusString = "Public";
                }

                let resizedImage = await resizeFile(state.selectedImage.file);

                const formData = new FormData();

                formData.append("is_user_upload_image", !state.selectedImage.default)
                formData.append("page_title", state.name)
                formData.append("fundraising_goal", state.goal)
                formData.append("currency_id", state.selectedCurrency.id)
                formData.append("country_id", state.selectedCurrency.countryId)
                formData.append("page_type", state.pageType)
                formData.append("campaign_id", state.campaign)
                formData.append("campaign_occassion_id", state.occasion)
                formData.append("description", state.missionStatement.storyWithTags ? state.missionStatement.storyWithTags : state.missionStatement.story)
                formData.append("user_id", UserService.getUser().id)
                formData.append("event_id", eventId)
                formData.append("product_id", productImpact.product_id | 0)
                formData.append("product_impact", productImpact.product_impact | 0)
                formData.append("product_quantity", productImpact.product_quantity | 0)
                formData.append("cover_image", state.selectedImage.default ? state.selectedImage.path : resizedImage)
                formData.append("is_special", state.fundraiseForFlood ? 1 : 0)
                formData.append("status", status)

                if (editView) {
                    await dispatch(fundRaiserPageAction.updateFundRaiserPage(formData, pageDetails.id));

                    if (pageDetails.status == DRAFT_PAGE && pageStatus == DRAFT_PAGE)
                        gtmEvent = GTM_EVENTS.PAGE_STORY_SAVED

                    else if (pageDetails.status == DRAFT_PAGE && pageStatus == PUBLIC_PAGE)
                        gtmEvent = GTM_EVENTS.PAGE_PUBLISHED

                    else gtmEvent = GTM_EVENTS.PAGE_STORY_UPDATED
                }
                else {
                    await dispatch(fundRaiserPageAction.createFundRaiserPage(formData))
                    gtmEvent = pageStatus == DRAFT_PAGE ? GTM_EVENTS.PAGE_STORY_SAVED : GTM_EVENTS.PAGE_PUBLISHED
                }

                Tracking.trackGTM({
                    event: gtmEvent,
                    page_name: state.name,
                    campaign_name: campaigns.find(campaign => campaign.id == state.campaign)?.name || null,
                    occasion_name: occasions.find(occasion => occasion.id == state.occasion)?.name || null,
                    country: state.selectedCurrency.name,
                    goal_amount: state.goal,
                    fundraiser_type: state.pageType == SINGLE_OWNER_PAGE ? "Individual Page" : "Team Page",
                    visiblity: pageStatusString,
                })

                pageStatus == PUBLIC_PAGE ? setPublishFundraiserInitiated(false) : setDraftingFundraiserInitiated(false);

                break;

            default:
                break;
        }
    }

    useNonInitialEffect(() => {
        if (editView && pageDetails)
            setState({
                ...state,
                name: pageDetails?.page_title || "",
                goal: pageDetails?.fundraising_goal || "",
                // titleSlug: slugify(pageDetails.page_title),
                selectedCurrency: {
                    id: pageDetails?.currency_id || DEFAULT_COUNTRY.id,
                    name: pageDetails?.country_name || DEFAULT_COUNTRY.name,
                    countryId: pageDetails?.country_id || DEFAULT_COUNTRY.countryId
                },
                pageType: pageDetails?.page_type || state.pageType,
                keepPagePrivate: pageDetails?.status == PRIVATE_PAGE || state.keepPagePrivate,
                campaign: pageDetails?.campaign_id || state.campaign,
                occasion: pageDetails?.campaign_occassion_id || state.occasion,
                missionStatement: { ...state.missionStatement, story: pageDetails?.description || state.missionStatement.story },
                selectedImage: {
                    ...state.selectedImage,
                    path: pageDetails?.cover_image || state.selectedImage.path,
                },
                fundraiseForFlood: pageDetails?.is_special ? pageDetails?.is_special == 1 : state.fundraiseForFlood
            })
    }, [Object.keys(pageDetails).length])

    useEffect(() => {
        dispatch(fundRaiserPageAction.getEventId());
        dispatch(fundRaiserPageAction.getUserDefaultCurrency());
    }, [])

    const { handleChange, handleSubmit } = useForm(publishFundraiser, validateFundraiserForm, handleStateChange, { ...state, tab: selectedTab });


    useNonInitialEffect(() => {
        if (errors.name || errors.goal)
            changeSelectedTab(BASIC_INFO)
    }, [errors])

    return <StyledFundraiserForm className="App-display-height">
        <Header />
        <Container component="main" className="App-content">
            <Box className="HeadingBox">
                <Typography className="Heading" variant="h4">{editView ? "Update" : "Create"} your fundraiser</Typography>
                <Typography>Get your page up and ready to accept donations! Fill out your information below to create a fundraising page. You can always edit these later.</Typography>
            </Box>

            <TabContext value={selectedTab}>
                <Box className="FundraiserFormBox">
                    <Box className="FundraiserTabs" centered>
                        <Tab id={BASIC_INFO} onClick={updateSelectedTab} className={selectedTab == BASIC_INFO ? "selected" : null} label="Basic Info" icon={<InfoIcon />} iconPosition="start" />
                        <Divider orientation="vertical" variant="middle" flexItem />
                        <Tab id={TELL_A_STORY} onClick={updateSelectedTab} className={selectedTab == TELL_A_STORY ? "selected" : null} label="Tell a Story" icon={<MegaPhoneIcon />} iconPosition="start" />
                    </Box>
                    <Box className="TabsContent">
                        <TabPanel value={BASIC_INFO}>
                            <BasicInfoTab state={state} handleChange={handleChange} validationErrors={errors} editView={editView} />
                        </TabPanel>
                        <TabPanel value={TELL_A_STORY} >
                            <TellStoryTab setState={setState} state={state} handleChange={handleChange} validationErrors={errors} editView={editView} />
                        </TabPanel>
                        <Stack justifyContent={'end'} flexDirection={'row'} gap={2} sx={{ mt: '24px' }}>
                            {mapPageButtons()}
                        </Stack>
                    </Box>
                </Box>
            </TabContext>
        </Container>
        <Footer />
    </StyledFundraiserForm>
}